import difference from 'lodash/difference';
import union from 'lodash/union';
import type { CheckboxSelectEntry, Options } from '@atlassian/jira-filters/src/common/types.tsx';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import {
	type DeploymentEnvironment as Environment,
	type DeploymentEnvironmentType as EnvironmentType,
	DeploymentEnvironmentTypeOptions as EnvironmentTypeOptions,
	type EnvironmentTypeConcatName,
} from '../../../../common/types';
import { useEnvironmentTypeConcatNamesFilter } from '../../../../controllers/top-filter';
import { useAllEnvironments } from './all-environments';
import {
	type EntryId,
	entryIdFromEnvironmentType,
	entryIdFromEnvironmentTypeConcatName,
	entryIdFromRawString,
	entryIdToRawString,
	matchEntryId,
	composeEnvironmentKey,
	extractEnvNameFromEnvironmentKey,
	entryIdFromEnvironment,
} from './entry-id';
import messages from './messages';

// Determines the order the environment types will be displayed in the menu
const orderedEnvironmentTypes: EnvironmentType[] = [
	EnvironmentTypeOptions.PRODUCTION,
	EnvironmentTypeOptions.STAGING,
	EnvironmentTypeOptions.TESTING,
	EnvironmentTypeOptions.DEVELOPMENT,
	EnvironmentTypeOptions.UNMAPPED,
];

const hasType =
	(type: EnvironmentType) =>
	(environment: Environment): boolean =>
		environment.type === type;

export const getCheckboxEntries = (
	allEnvironments: Environment[],
	formatForDisplay: (arg1: EnvironmentType) => string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formatMessage: (descriptor: MessageDescriptor, values?: Record<string, any>) => string,
): Options => {
	const toEnvironmentTypeCheckboxEntry = (type: EnvironmentType): CheckboxSelectEntry => ({
		label: formatMessage(messages.allEnvironmentSelectLabel, {
			envType: formatForDisplay(type),
		}),
		value: entryIdFromEnvironmentType(type),
	});

	const toEnvironmentNameCheckboxEntry = (environment: Environment): CheckboxSelectEntry => ({
		label: environment.displayName,
		value: entryIdFromEnvironment(environment),
	});

	const orderedExistingEnvTypes = orderedEnvironmentTypes.filter((type) =>
		allEnvironments.some(hasType(type)),
	);

	const entries: Options = orderedExistingEnvTypes.map((type) =>
		toEnvironmentTypeCheckboxEntry(type),
	);
	return entries.concat(
		orderedExistingEnvTypes.map((type) => ({
			label: formatForDisplay(type),
			options: allEnvironments.filter(hasType(type)).map(toEnvironmentNameCheckboxEntry),
		})),
	);
};

export const getSelectedEntryIds = (
	environmentTypeConcatNamesFilter: EnvironmentTypeConcatName[],
	allEnvironments: Environment[],
): EntryId[] => {
	const areAllEnvironmentsWithTypeSelected = (type: EnvironmentType) => {
		const environmentsWithType = allEnvironments.filter(hasType(type));
		return (
			environmentsWithType.length > 0 &&
			environmentsWithType.every((environment) =>
				environmentTypeConcatNamesFilter.includes(
					composeEnvironmentKey(environment.type, environment.displayName),
				),
			)
		);
	};

	const environmentTypeEntryIds = orderedEnvironmentTypes
		.filter(areAllEnvironmentsWithTypeSelected)
		.map(entryIdFromEnvironmentType);

	return [
		...environmentTypeEntryIds,
		...environmentTypeConcatNamesFilter.map(entryIdFromEnvironmentTypeConcatName),
	];
};

const getEnvironmentNames = (entryIds: EntryId[]): EnvironmentTypeConcatName[] =>
	entryIds.flatMap((entryId) =>
		matchEntryId(entryId, {
			environmentName: (name) => [name],
			environmentType: () => [],
		}),
	);

const getEnvironmentTypes = (entryIds: EntryId[]): EnvironmentType[] =>
	entryIds.flatMap((entryId) =>
		matchEntryId(entryId, {
			environmentName: () => [],
			environmentType: (type) => [type],
		}),
	);

const getEnvironmentNamesForType = (
	type: EnvironmentType,
	environments: Environment[],
): EnvironmentTypeConcatName[] =>
	environments
		.filter(hasType(type))
		.map((environment) => composeEnvironmentKey(environment.type, environment.displayName));

export const getNextEnvironmentTypeConcatNamesFilter = (
	prevSelectedEntryIds: EntryId[],
	nextSelectedEntryIds: EntryId[],
	allEnvironments: Environment[],
): EnvironmentTypeConcatName[] => {
	const nextSelectedEnvironmentNames = getEnvironmentNames(nextSelectedEntryIds);
	const nextSelectedEnvironmentTypes = getEnvironmentTypes(nextSelectedEntryIds);
	const prevSelectedEnvironmentTypes = getEnvironmentTypes(prevSelectedEntryIds);

	// Determine which environment types the user just *deselected*
	const environmentNamesForNewlyDeselectedEnvironmentTypes = difference(
		prevSelectedEnvironmentTypes,
		nextSelectedEnvironmentTypes,
	).flatMap((type) => getEnvironmentNamesForType(type, allEnvironments));

	// Determine which environment types the user just *selected*
	const environmentNamesForNewlySelectedEnvironmentTypes = difference(
		nextSelectedEnvironmentTypes,
		prevSelectedEnvironmentTypes,
	).flatMap((type) => getEnvironmentNamesForType(type, allEnvironments));

	// Determine the next environment names filter by:
	// 1. Starting with the next selected names.
	// 2. Removing names that correspond to environment types that were just *deselected*.
	// 3. Adding names that correspond to environment types that were just *selected*.
	return union(
		difference(nextSelectedEnvironmentNames, environmentNamesForNewlyDeselectedEnvironmentTypes),
		environmentNamesForNewlySelectedEnvironmentTypes,
	);
};

export const useFormatEnvironmentType = () => {
	const { formatMessage } = useIntl();

	const labels = {
		PRODUCTION: formatMessage(messages.production),
		STAGING: formatMessage(messages.staging),
		TESTING: formatMessage(messages.testing),
		DEVELOPMENT: formatMessage(messages.development),
		UNMAPPED: formatMessage(messages.unmapped),
	};

	return (type: EnvironmentType): string => labels[type];
};

type SelectState = {
	entries: Options;
	selectedEntryIds: string[];
	onChange: (arg1: CheckboxSelectEntry[]) => void;
};

export const useSelectState = (
	formatForDisplay: (arg1: EnvironmentType) => string,
): SelectState => {
	const allEnvironments = useAllEnvironments();
	const { formatMessage } = useIntl();
	const entries = getCheckboxEntries(allEnvironments, formatForDisplay, formatMessage);
	const [environmentTypeConcatNamesFilter, actions] = useEnvironmentTypeConcatNamesFilter();
	const selectedEntryIds = getSelectedEntryIds(environmentTypeConcatNamesFilter, allEnvironments);

	const onChange = (nextSelectedEntries: CheckboxSelectEntry[]) => {
		const nextSelectedEntryIds = nextSelectedEntries.map((entry) =>
			entryIdFromRawString(entry.value),
		);
		const nextEnvironmentTypeConcatNamesFilter = getNextEnvironmentTypeConcatNamesFilter(
			selectedEntryIds,
			nextSelectedEntryIds,
			allEnvironments,
		);

		actions.setEnvironmentFilter(
			nextEnvironmentTypeConcatNamesFilter.map(extractEnvNameFromEnvironmentKey),
		);
		actions.setEnvironmentTypeConcatNameFilter(nextEnvironmentTypeConcatNamesFilter);
	};

	return {
		entries,
		selectedEntryIds: selectedEntryIds.map(entryIdToRawString),
		onChange,
	};
};
